/** Sendepot docs IA. Single source of truth: powers the sidebar nav,
 * breadcrumbs, prev/next links, and the route table. Adding a page is a
 * matter of (1) appending it here, (2) writing a component in
 * content.tsx with the matching slug. */

export type DocPage = {
  slug: string;          // last URL segment, e.g. "quick-start"
  title: string;         // sidebar + page heading
  blurb?: string;        // optional one-line subtitle shown under the H1
};

export type DocSection = {
  slug: string;          // section URL segment, e.g. "getting-started"
  title: string;         // sidebar group label
  pages: DocPage[];
};

export const DOCS: DocSection[] = [
  {
    slug: 'welcome',
    title: 'Welcome',
    pages: [
      { slug: 'read-first', title: 'Read first', blurb: 'What Sendepot is and how it works.' },
    ],
  },
  {
    slug: 'getting-started',
    title: 'Getting started',
    pages: [
      { slug: 'quick-start', title: 'Quick start', blurb: 'From signup to first inbox in 10 minutes.' },
      { slug: 'concepts', title: 'Concepts', blurb: 'License, tenants, mailboxes — the mental model.' },
      { slug: 'pricing', title: 'Pricing' },
    ],
  },
  {
    slug: 'agents',
    title: 'Integrate with agents',
    pages: [
      { slug: 'overview', title: 'Overview', blurb: 'When to use the API, MCP, or the UI.' },
      { slug: 'claude-desktop', title: 'Claude Desktop' },
      { slug: 'claude-code', title: 'Claude Code' },
      { slug: 'cursor', title: 'Cursor & other MCP clients' },
      { slug: 'http-quickstart', title: 'HTTP API quickstart' },
      { slug: 'tools', title: 'MCP tool reference' },
    ],
  },
  {
    slug: 'microsoft-tenants',
    title: 'Microsoft tenants',
    pages: [
      { slug: 'what-they-are', title: 'What they are' },
      { slug: 'where-to-get', title: 'Where to get them', blurb: 'Four ways to source tenants — pros, cons, costs.' },
      { slug: 'pre-flight', title: 'Pre-flight checklist' },
      { slug: 'become-a-partner', title: 'Becoming a Microsoft Partner', blurb: 'Advanced — for high-volume operators.' },
    ],
  },
  {
    slug: 'sequencers',
    title: 'Sequencers',
    pages: [
      { slug: 'overview', title: 'Overview' },
      { slug: 'instantly', title: 'Connecting Instantly' },
      { slug: 'smartlead', title: 'Connecting Smartlead', blurb: 'Including the OAuth URL trick.' },
      { slug: 'multi-account', title: 'Multiple accounts on one sequencer' },
    ],
  },
  {
    slug: 'provisioning',
    title: 'Provisioning',
    pages: [
      { slug: 'first-tenant', title: 'Your first tenant' },
      { slug: 'phases', title: 'What runs behind the scenes', blurb: 'The four customer-facing stages.' },
      { slug: 'bulk', title: 'Bulk creating tenants' },
      { slug: 'progress', title: 'Watching progress' },
    ],
  },
  {
    slug: 'best-practices',
    title: 'Best practices',
    pages: [
      { slug: 'reply-rates', title: 'Reply-rate fundamentals' },
      { slug: 'outlook-vs-google', title: 'Outlook vs Google' },
      { slug: 'warmup', title: 'Warm-up schedules' },
      { slug: 'spintax', title: 'Spintax & copy variation' },
      { slug: 'domain-rotation', title: 'Domain rotation' },
      { slug: 'sending-to-outlook', title: 'Sending TO Outlook recipients' },
      { slug: 'avoid-mass-tests', title: 'Don’t mass-test deliverability' },
    ],
  },
  {
    slug: 'tenant-blocking',
    title: 'Tenant-level blocking',
    pages: [
      { slug: 'overview', title: 'Why it happens', blurb: 'The 550 5.7.705 error and how to avoid it.' },
      { slug: 'recovery', title: 'What to do when blocked' },
    ],
  },
  {
    slug: 'mailboxes',
    title: 'Mailbox management',
    pages: [
      { slug: 'list', title: 'Listing mailboxes' },
      { slug: 'reconnect', title: 'Reconnecting failed OAuth' },
      { slug: 'reset-password', title: 'Resetting passwords' },
      { slug: 'export-csv', title: 'Bulk CSV export' },
    ],
  },
  {
    slug: 'domain-swap',
    title: 'Domain swap',
    pages: [
      { slug: 'overview', title: 'When and how' },
    ],
  },
  {
    slug: 'troubleshooting',
    title: 'Troubleshooting',
    pages: [
      { slug: 'inbox-disconnects', title: 'Inboxes disconnecting' },
      { slug: 'stuck-tenants', title: 'Tenant stuck running' },
      { slug: 'aadsts-errors', title: 'Common AADSTS error codes' },
    ],
  },
  {
    slug: 'account',
    title: 'Account & billing',
    pages: [
      { slug: 'sign-in', title: 'Magic-link sign-in' },
      { slug: 'workspace', title: 'Workspace settings' },
      { slug: 'trial-to-paid', title: 'Plans & billing' },
    ],
  },
];

export type FlatPage = {
  sectionSlug: string;
  sectionTitle: string;
  page: DocPage;
  href: string;          // /docs/<section>/<page>
};

export function flatten(): FlatPage[] {
  const out: FlatPage[] = [];
  for (const s of DOCS) {
    for (const p of s.pages) {
      out.push({
        sectionSlug: s.slug,
        sectionTitle: s.title,
        page: p,
        href: `/docs/${s.slug}/${p.slug}`,
      });
    }
  }
  return out;
}

export function findPage(sectionSlug: string, pageSlug: string): FlatPage | null {
  return flatten().find(p => p.sectionSlug === sectionSlug && p.page.slug === pageSlug) || null;
}

export function neighbors(sectionSlug: string, pageSlug: string): { prev: FlatPage | null; next: FlatPage | null } {
  const flat = flatten();
  const idx = flat.findIndex(p => p.sectionSlug === sectionSlug && p.page.slug === pageSlug);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? flat[idx - 1] : null,
    next: idx < flat.length - 1 ? flat[idx + 1] : null,
  };
}
